import React, { createRef, FC, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { SingleValue } from 'react-select';
import { DialogOverlay } from 'src/components/pages/dashboard/intro/dialogDel';
import Loader from 'src/components/widgets/loader';
import Stack from 'src/components/widgets/stack/stack';
import { ValidationMessageStyled } from 'src/components/widgets/validation/validationMessage';
import { InputWithValidation, Separator } from 'src/shared/globals';
import _ from 'lodash';
import { stringFormat, T, toLocalID, validateInputSecurity } from 'src/shared/helpers';
import InfoCircleIcon from 'src/shared/material_icons/action/info.svg';
import { validateRequired, validationContext, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { Columns } from '../../../../widgets/columns';
import { BasicDialogWindow, Dialog } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import { Input } from '../../../../widgets/input';
import { showNotification } from '../../../../widgets/notificationToaster';
import { AdvancedSelect } from '../../../../widgets/select';
import { ModelColor } from '../../state/model/modelColor';
import { AdditionalCode, ModelColorMapping } from '../../state/model/modelColorMapping';
import { editVariantDialogQuery } from './__generated__/editVariantDialogQuery.graphql';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { ColorAttribute, matchColorsAttributes, merchantProductOptionType } from './helpers';

type EditVariantDialogProps = {
    name?: string;
    buttonLabel?: string;
    mainSalesChannelID: string;
    mainSalesChannelRemoteUrl: string;
    modelColor: ModelColor;
    modelColorCode: ColorAttribute[];
    modelOptions: merchantProductOptionType[];
    additionalCodes: AdditionalCode[];
    colorMappings: ModelColorMapping[];
    mainStoreVariantsRemoved?: boolean;
    importedOptionIDs: string[];
    otherVariants: ModelColor[];
    onDelete: () => void;
    onConfirm: (
        value: string,
        attributes: ColorAttribute[] | null | undefined,
        modelColorMapping: ModelColorMapping | null | undefined,
        onError: () => void
    ) => void;
};

type EditVariantDialogMerchantOptionsWrapperProps = {
    sku: string;
    salesChannelId: string;
    children: (items: merchantProductOptionType[]) => React.ReactNode;
};

const DialogOverlayEditVariant = styled(DialogOverlay)`
    z-index: 9999;
    justify-content: center;
`;

const EditVariantDialogWindow = styled(BasicDialogWindow)`
    min-height: min(520px, calc(100vh - 80px));
`;

const HeaderColumns = styled(Columns)`
    strong {
        margin-bottom: 7px;
    }
`;

const AttributeSeparator = styled(Separator)`
    margin: 10px 0px 28px !important;
`;

const NotImportedSeparator = styled(Separator)`
    margin: 5px 0px !important;
`;

const NotImportedHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    line-height: 16px;

    > span {
        color: #757575;
    }
`;

// Read-only summary of an attribute deleted on the store: plain text, not a field, since nothing can be done on it
const RemovedAttribute = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    line-height: 18px;

    > span:first-child {
        font-weight: 600;
    }
`;

const RemovedAttributeValue = styled.span`
    color: #757575;
    text-decoration: line-through;
`;

const RemovedAttributeTag = styled.span`
    padding: 0 6px;
    border-radius: 4px;
    background: #f1f3f5;
    color: #757575;
    font-size: 11px;
`;

// Store value already used by another variant with the current attributes: listed but not selectable
const AssignedOption = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    > span:first-child {
        color: #9e9e9e;
    }
`;

// Only the value fades: the tag keeps full contrast so it stays readable
const AssignedTag = styled.span`
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 4px;
    background: #fff4e5;
    color: #b45309;
    font-size: 11px;
    font-weight: 600;
`;

const NoVariantsWarning = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 5px 0 15px;
    font-size: 12px;
    color: #d9534f;
    line-height: 16px;

    > svg {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
        margin-top: 1px;
        fill: #d9534f;
    }
`;

const EditVariantDialogContent: FC<EditVariantDialogProps> = ({
    name,
    buttonLabel,
    mainSalesChannelID,
    mainSalesChannelRemoteUrl,
    modelColor,
    modelColorCode,
    modelOptions,
    additionalCodes,
    colorMappings,
    mainStoreVariantsRemoved,
    importedOptionIDs,
    otherVariants,
    onDelete,
    onConfirm
}) => {
    const validation = useContext(validationContext);

    // Same rule used when variants are imported: attribute values joined by ' - ', in store attribute order
    const buildVariantName = (variantAttributes: ColorAttribute[]) =>
        _.sortBy(variantAttributes, (a) => modelOptions.findIndex((mo) => mo.optionID == a.Id))
            .map((a) => a.Value.Label)
            .join(' - ');

    const initialAttributes = modelColorCode.filter((a) => modelOptions.some((mo) => mo.optionID == a.Id));
    // Attributes deleted on the store: shown read-only, they are dropped from the variant on confirm
    const removedAttributes = modelOptions.length > 0 ? modelColorCode.filter((a) => !modelOptions.some((mo) => mo.optionID == a.Id)) : [];
    // The name follows the attributes only while it is the generated one, never once the customer customizes it
    const isGeneratedName = name === modelColorCode.map((a) => a.Value.Label).join(' - ');

    // State
    const [variantName, setVariantName] = useState(
        isGeneratedName && removedAttributes.length > 0 && initialAttributes.length > 0 ? buildVariantName(initialAttributes) : name ?? ''
    );
    const [isNameAutoUpdated, setIsNameAutoUpdated] = useState(isGeneratedName);
    const [dialogMode, setDialogMode] = useState<'EditVariant' | 'EditMapping'>('EditVariant');
    const [attributes, setAttributes] = useState<ColorAttribute[]>(initialAttributes);
    const [salesChannelSelectedID, setSalesChannelSelectedID] = useState(mainSalesChannelID);
    const [skuSelected, setSkuSelected] = useState(additionalCodes.find((ac) => ac.salesChannelId == mainSalesChannelID)?.code ?? '');
    const [modelColorMappings, setModelColorMappings] = useState<ModelColorMapping[]>(colorMappings);
    const [isEditingVariantInProgress, setIsEditingVariantInProgress] = useState(false);

    const inputRef = createRef<HTMLInputElement>();
    const merchantProductOptionsRef = useRef<merchantProductOptionType[]>([]);

    const notMappedOption = { value: '', label: T._('Not mapped', 'Admin') };
    // A variant must keep at least one attribute when the store has options to map
    const isMissingAttributes = dialogMode == 'EditVariant' && modelOptions.length > 0 && !attributes.length;
    // Store options no variant uses yet are listed apart, so the customer knows mapping them imports them for this variant only
    const importedModelOptions = modelOptions.filter((mo) => importedOptionIDs.includes(mo.optionID));
    const notImportedModelOptions = modelOptions.filter((mo) => !importedOptionIDs.includes(mo.optionID));
    // Two variants cannot share the same attributes: spot it here instead of failing on confirm
    const duplicateVariant =
        dialogMode == 'EditVariant' && attributes.length > 0 ? otherVariants.find((v) => v.code && matchColorsAttributes(v.code, attributes)) : undefined;
    // The duplicate comes from attributes deleted on the store: the variant has nothing left of its own, so deleting it is the way out
    const isDuplicateAfterStoreRemoval = !!duplicateVariant && removedAttributes.length > 0;

    const isValidStoreValue = (a: ColorAttribute) => modelOptions.some((mo) => mo.optionID == a.Id && mo.values.some((v) => v.valueID == a.Value.Id));
    // The saved attributes no longer fit the store: an attribute or a value was deleted there
    const isOutOfStore = modelOptions.length > 0 && (removedAttributes.length > 0 || !initialAttributes.length || !initialAttributes.every(isValidStoreValue));
    // Every combination of store values is already taken by the other variants: there is nothing left to remap this one to
    const usedCombinations = new Set(
        otherVariants
            .map((v) => JSON.parse(v.code || '[]') as ColorAttribute[])
            .filter((variantAttributes) => variantAttributes.length === modelOptions.length && variantAttributes.every(isValidStoreValue))
            .map((variantAttributes) => _.sortBy(variantAttributes.map((a) => `${a.Id}:${a.Value.Id}`)).join('|'))
    );
    const availableCombinations = modelOptions.reduce((count, mo) => count * mo.values.length, 1);
    const isUnmappable = dialogMode == 'EditVariant' && isOutOfStore && usedCombinations.size >= availableCombinations;
    // In both cases the variant has no place left among the store variants, so deleting it replaces confirming
    const isDeleteSuggested = isUnmappable || isDuplicateAfterStoreRemoval;

    const formatAttributes = (items: ColorAttribute[]) => items.map((a) => `${a.Label}: ${a.Value.Label}`).join(', ');
    // Reads as a sentence: "A, B and C"
    const joinLabels = (labels: string[]) =>
        labels.length > 1 ? `${labels.slice(0, -1).join(', ')} ${T._('and', 'Admin')} ${labels[labels.length - 1]}` : labels[0] ?? '';
    // Values of the variant the store no longer has; none means each value exists but not in this combination
    // Removed attributes are told apart, since there the whole store variant is gone, not a single value
    const missingValues = modelColorCode.filter((a) => !isValidStoreValue(a) && !removedAttributes.some((r) => r.Id == a.Id));

    // With a single store attribute, unmapping it would leave the variant with no attributes at all
    const canUnmapAttributes = modelOptions.length > 1;

    // A value is already assigned when picking it, with the other attributes as they are now, gives the combination of another variant
    const isValueAssigned = (opt: merchantProductOptionType, valueID: string) => {
        const candidate = [...attributes.filter((a) => a.Id != opt.optionID), { Id: opt.optionID, Label: opt.label, Value: { Id: valueID, Label: '' } }];
        return otherVariants.some((v) => v.code && matchColorsAttributes(v.code, candidate));
    };

    const renderAttributeSelect = (opt: merchantProductOptionType) => {
        const valueOptions = opt.values.map((val) => ({ value: val.valueID, label: val.label, isAssigned: isValueAssigned(opt, val.valueID) }));
        const options = canUnmapAttributes ? [{ ...notMappedOption, isAssigned: false }, ...valueOptions] : valueOptions;
        const attribute = attributes.find((x) => x.Id == opt.optionID);
        return (
            <FormControl key={opt.optionID} label={opt.label}>
                <AdvancedSelect
                    onChange={(selectedOption) => handleChangeAttribute(opt, selectedOption?.value ?? '')}
                    menuPosition='fixed'
                    value={attribute ? { value: attribute.Value.Id, label: attribute.Value.Label } : canUnmapAttributes ? notMappedOption : null}
                    placeholder={T._('Select a value', 'Admin')}
                    options={options}
                    isOptionDisabled={(option) => option.isAssigned}
                    formatOptionLabel={(option, { context }) =>
                        context === 'menu' && option.isAssigned ? (
                            <AssignedOption>
                                <span>{option.label}</span>
                                <AssignedTag>{T._('Already assigned', 'Admin')}</AssignedTag>
                            </AssignedOption>
                        ) : (
                            option.label
                        )
                    }
                />
            </FormControl>
        );
    };

    // Callbacks
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleConfirm();
    };

    const handleChangeAttribute = (merchantOption: merchantProductOptionType, value: string) => {
        let newAttributes: ColorAttribute[];
        if (!value) {
            newAttributes = attributes.filter((x) => x.Id !== merchantOption.optionID);
        } else {
            const merchantOptionSelected = merchantOption.values.find((x) => x.valueID === value);
            if (!merchantOptionSelected) return;

            // The attribute label is refreshed too, in case it was renamed on the store
            const newAttribute = {
                Id: merchantOption.optionID,
                Label: merchantOption.label,
                Value: { Id: merchantOptionSelected.valueID, Label: merchantOptionSelected.label }
            };
            newAttributes = attributes.some((x) => x.Id == merchantOption.optionID)
                ? attributes.map((x) => (x.Id == merchantOption.optionID ? newAttribute : x))
                : [...attributes, newAttribute];
        }

        setAttributes(newAttributes);
        if (isNameAutoUpdated && newAttributes.length > 0) setVariantName(buildVariantName(newAttributes));
    };

    const handleConfirm = () => {
        if (isMissingAttributes || duplicateVariant) return;

        if (validation.isValid() === false) {
            showNotification({
                type: 'danger',
                message: T._('Some fields are invalid. Please correct them before confirming.', 'Admin'),
                dismiss: {
                    duration: 3000
                }
            });
            return;
        }

        if (attributes.length) {
            setIsEditingVariantInProgress(true);

            const modelColorMappingsForConfirm = getModelColorMappingsForMerchantOptions(merchantProductOptionsRef.current);
            let modelColorMapping = modelColorMappingsForConfirm.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
            if (modelColorMapping) {
                let merchantProdutAttributesMapping = JSON.parse(modelColorMapping.merchantCode || '[]') as ColorAttribute[];
                if ([...new Set(merchantProdutAttributesMapping.map((mpa) => mpa.Id))].length != merchantProdutAttributesMapping.length) {
                    showNotification({
                        type: 'danger',
                        message: (
                            <>
                                {T._(
                                    'Unable to update product variants mapping. Every attributes must be mapped with different merchant product attributes',
                                    'Admin'
                                )}
                            </>
                        ),
                        dismiss: {
                            duration: 3000
                        }
                    });
                    setIsEditingVariantInProgress(false);
                    return;
                }
            }

            onConfirm(variantName, attributes, modelColorMapping, () => {
                setIsEditingVariantInProgress(false);
            });
        } else {
            // update only the name
            onConfirm(variantName, null, null, () => {
                setIsEditingVariantInProgress(false);
            });
        }
    };

    const onChangeSourceVariants = (item: SingleValue<{ value: string; label: string }>) => {
        let newSalesChannelID = item?.value ?? mainSalesChannelID;
        setSalesChannelSelectedID(newSalesChannelID);
        setSkuSelected(additionalCodes.find((ac) => ac.salesChannelId == newSalesChannelID)?.code ?? '');
        merchantProductOptionsRef.current = [];
        toLocalID(newSalesChannelID) == toLocalID(mainSalesChannelID) ? setDialogMode('EditVariant') : setDialogMode('EditMapping');
    };

    const onChangeAttributeMapping = (
        item: SingleValue<{ value: string; label: string }>,
        indexAttributeOfflineProduct: number,
        merchantProductOptions: merchantProductOptionType[],
        currentModelColorMappings: ModelColorMapping[]
    ) => {
        let query = currentModelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
        if (!query) return;

        let newModelColorMapping = { ...query };
        let newModelColorMappings = [...currentModelColorMappings.filter((mcm) => toLocalID(mcm.salesChannelID) != toLocalID(salesChannelSelectedID))];

        let merchantColorAttribute = JSON.parse(newModelColorMapping.merchantCode || '[]') as ColorAttribute[];
        let option = merchantProductOptions.find((mo) => mo.optionID == item?.value)?.values[0]!;

        merchantColorAttribute[indexAttributeOfflineProduct].Id = item?.value!;
        merchantColorAttribute[indexAttributeOfflineProduct].Label = item?.label!;
        merchantColorAttribute[indexAttributeOfflineProduct].Value.Label = option.label;
        merchantColorAttribute[indexAttributeOfflineProduct].Value.Id = option.valueID;
        newModelColorMapping.merchantCode = JSON.stringify(merchantColorAttribute);
        newModelColorMappings.push(newModelColorMapping);

        setModelColorMappings(newModelColorMappings);
    };

    const onChangeOptionMapping = (
        item: SingleValue<{ value: string; label: string }>,
        indexAttributeOfflineProduct: number,
        merchantProductOptions: merchantProductOptionType[],
        currentModelColorMappings: ModelColorMapping[]
    ) => {
        let query = currentModelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
        if (!query) return;

        let newModelColorMapping = { ...query };
        let newModelColorMappings = [...currentModelColorMappings.filter((mcm) => toLocalID(mcm.salesChannelID) != toLocalID(salesChannelSelectedID))];

        let merchantColorAttribute = JSON.parse(newModelColorMapping.merchantCode || '[]') as ColorAttribute[];
        let option = merchantProductOptions
            .find((mo) => mo.optionID == merchantColorAttribute[indexAttributeOfflineProduct].Id)
            ?.values.find((v) => v.valueID == item?.value)!;

        merchantColorAttribute[indexAttributeOfflineProduct].Value.Label = option?.label ?? 'No map';
        merchantColorAttribute[indexAttributeOfflineProduct].Value.Id = option?.valueID ?? '';
        newModelColorMapping.merchantCode = JSON.stringify(merchantColorAttribute);

        newModelColorMappings.push(newModelColorMapping);
        setModelColorMappings(newModelColorMappings);
    };

    const getModelColorMappingsForMerchantOptions = (items: merchantProductOptionType[]) => {
        if (dialogMode != 'EditMapping') return modelColorMappings;

        let modelColorMapping = modelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
        if (items.length >= modelColorCode.length) {
            let newModelColorMapping = modelColorMapping
                ? { ...modelColorMapping }
                : ({
                      colorID: modelColor.colorID,
                      merchantCode: '[]',
                      salesChannelID: salesChannelSelectedID,
                      productSKU: '',
                      productColor: {
                          code: modelColor.code,
                          guid: modelColor.guid,
                          colorID: modelColor.colorID,
                          name: modelColor.name
                      }
                  } as ModelColorMapping);

            let merchantMappingCode = JSON.parse(modelColorMapping?.merchantCode || '[]') as ColorAttribute[];
            let newMerchantMappingCode = [...merchantMappingCode];

            if (!modelColorMapping) {
                items.forEach((attribute) => {
                    newMerchantMappingCode.push({
                        Id: attribute.optionID,
                        Label: attribute.label,
                        Value: {
                            Id: '',
                            Label: 'No map'
                        }
                    });
                });
            } else if (merchantMappingCode.length < modelColorCode.length) {
                let newAttributes = [...items.filter((item) => merchantMappingCode.some((ncm) => ncm.Id != item.optionID))];
                newAttributes.forEach((newAttribute) => {
                    newMerchantMappingCode.push({
                        Id: newAttribute.optionID,
                        Label: newAttribute.label,
                        Value: {
                            Id: newAttribute.values[0].valueID,
                            Label: newAttribute.values[0].label
                        }
                    });
                });
            }

            let newModelColorMappings = [...modelColorMappings.filter((m) => toLocalID(m.salesChannelID) != toLocalID(salesChannelSelectedID))];

            newModelColorMapping.merchantCode = JSON.stringify(newMerchantMappingCode);
            newModelColorMappings.push(newModelColorMapping);
            return newModelColorMappings;
        } else if (items.length == 0) {
            let newModelColorMapping =
                (modelColor.colorID ?? -1) > 0
                    ? {
                          colorID: modelColor.colorID ?? -1,
                          salesChannelID: salesChannelSelectedID,
                          merchantCode: '[]'
                      }
                    : ({
                          colorID: modelColor.colorID ?? -1,
                          salesChannelID: salesChannelSelectedID,
                          merchantCode: '[]',
                          productColor: {
                              code: modelColor.code,
                              colorID: modelColor.colorID,
                              guid: modelColor.guid,
                              name: modelColor.name
                          }
                      } as ModelColorMapping);

            let newModelColorMappings = [...modelColorMappings.filter((m) => toLocalID(m.salesChannelID) != toLocalID(salesChannelSelectedID))];
            newModelColorMappings.push(newModelColorMapping);
            return newModelColorMappings;
        }

        return modelColorMappings;
    };

    // Option combo source variants
    const sourceVariantOptions = [
        {
            value: mainSalesChannelID,
            label: mainSalesChannelRemoteUrl
        },

        ...additionalCodes.map((ac) => {
            return {
                value: ac.salesChannelId,
                label: ac.remoteUrl ?? ''
            };
        })
    ];

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const dialogTitle = `${dialogMode == 'EditMapping' ? T._('Edit variant mapping', 'Admin') : T._('Edit variant', 'Admin')}: ${
        variantName || modelColor.name
    }`;

    const renderDialogContent = (merchantProductOptions: merchantProductOptionType[]) => {
        merchantProductOptionsRef.current = merchantProductOptions;

        const modelColorMappingsForRender = getModelColorMappingsForMerchantOptions(merchantProductOptions);

        // Options for mapping attributes combo
        const merchantProductAttributes = merchantProductOptions.map((mpo) => {
            return { label: mpo.label, value: mpo.optionID };
        });
        const merchantAttributeMapping = JSON.parse(
            modelColorMappingsForRender.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID))?.merchantCode || '[]'
        ) as ColorAttribute[];

        return (
            <Stack>
                {sourceVariantOptions.length > 1 && (
                    <FormControl label={T._('Source', 'Admin')}>
                        <AdvancedSelect
                            onChange={onChangeSourceVariants}
                            menuPosition={'fixed'}
                            value={sourceVariantOptions.find((o) => toLocalID(o.value) == toLocalID(salesChannelSelectedID))}
                            options={sourceVariantOptions}
                        ></AdvancedSelect>
                    </FormControl>
                )}

                {((toLocalID(salesChannelSelectedID) == toLocalID(mainSalesChannelID) && mainStoreVariantsRemoved) ||
                    (dialogMode == 'EditMapping' && merchantProductOptions.length == 0)) && (
                    <NoVariantsWarning>
                        <InfoCircleIcon />
                        <span>{T._('There seems to be an issue with the variants. Please verify them directly.', 'Admin')}</span>
                    </NoVariantsWarning>
                )}

                {dialogMode == 'EditVariant' ? (
                    <>
                        {isUnmappable && (
                            <NotificationBanner
                                type='WARNING'
                                title={T._('This variant no longer matches your store', 'Admin')}
                                content={
                                    <Stack $gap='8px'>
                                        <span>
                                            <strong>{T._('Zakeke variant:', 'Admin')}</strong>{' '}
                                            {modelColorCode.map((a) => `${a.Label}: ${a.Value.Label}`).join(' · ') || modelColor.name}
                                        </span>
                                        <span>
                                            {removedAttributes.length === 0 &&
                                                missingValues.length === 0 &&
                                                T._('This option combination is no longer available in your store.', 'Admin')}
                                            {removedAttributes.length > 0 &&
                                                stringFormat(
                                                    removedAttributes.length === 1
                                                        ? T._('The option {0} and its values are no longer available in your store.', 'Admin')
                                                        : T._('The options {0} and their values are no longer available in your store.', 'Admin'),
                                                    joinLabels(removedAttributes.map((a) => `“${a.Label}”`))
                                                )}{' '}
                                            {missingValues.length > 0 &&
                                                stringFormat(
                                                    missingValues.length === 1
                                                        ? T._('The value {0} is no longer available in your store.', 'Admin')
                                                        : T._('The values {0} are no longer available in your store.', 'Admin'),
                                                    joinLabels(missingValues.map((a) => stringFormat(T._('“{0}” for “{1}”', 'Admin'), a.Value.Label, a.Label)))
                                                )}{' '}
                                            {T._(
                                                'All remaining store variants are already mapped to other Zakeke variants, so there’s no match available for this one.',
                                                'Admin'
                                            )}
                                        </span>
                                        <span>
                                            {T._(
                                                'To keep it, add the missing value or combination to your store first. Otherwise, delete it to prevent the “Out-of-stock product variation” warning in the customizer.',
                                                'Admin'
                                            )}
                                        </span>
                                    </Stack>
                                }
                            />
                        )}
                        {isDuplicateAfterStoreRemoval && !isUnmappable && (
                            <NotificationBanner
                                type='WARNING'
                                title={stringFormat(T._('This variant is now the same as "{0}"', 'Admin'), duplicateVariant!.name)}
                                content={
                                    <Stack $gap='4px'>
                                        <span>
                                            {stringFormat(
                                                removedAttributes.length === 1
                                                    ? T._('The "{0}" attribute was removed from your store, so this variant went from {1} to {2}.', 'Admin')
                                                    : T._('The "{0}" attributes were removed from your store, so this variant went from {1} to {2}.', 'Admin'),
                                                removedAttributes.map((a) => a.Label).join('", "'),
                                                formatAttributes(modelColorCode),
                                                formatAttributes(attributes)
                                            )}
                                        </span>
                                        <span>
                                            {stringFormat(
                                                T._(
                                                    '"{0}" already uses these attributes, so this variant no longer has a match on your store. Delete it now, or later with the trash icon, to prevent the "Out-of-stock product variation." warning in the customizer.',
                                                    'Admin'
                                                ),
                                                duplicateVariant!.name
                                            )}
                                        </span>
                                    </Stack>
                                }
                            />
                        )}
                        <FormControl label={T._('Variant name', 'Admin')}>
                            <InputWithValidation
                                ref={inputRef}
                                onKeyDown={handleKeyDown}
                                value={variantName}
                                onChange={(e) => {
                                    setVariantName(e.currentTarget.value);
                                    setIsNameAutoUpdated(false);
                                }}
                                validationKey='validationVariantName'
                                validate={(value) => validateRequired(value) || validateInputSecurity(value).isValid === false}
                                message={`${
                                    validateRequired(variantName)
                                        ? T._('Required', 'Admin')
                                        : !validateInputSecurity(variantName).isValid
                                        ? T._(`Invalid characters detected: ${validateInputSecurity(variantName).invalidCharacters.join(', ')}`, 'Admin')
                                        : ''
                                }`}
                            />
                        </FormControl>

                        {importedModelOptions.map(renderAttributeSelect)}
                        {removedAttributes.length > 0 && (
                            <>
                                <NotImportedSeparator />
                                <NotImportedHeader>
                                    <strong>{T._('Removed from store', 'Admin')}</strong>
                                    <span>
                                        {T._(
                                            'These attributes no longer exist on the store: they will be removed from this variant when you confirm.',
                                            'Admin'
                                        )}
                                    </span>
                                </NotImportedHeader>
                                {removedAttributes.map((a) => (
                                    <RemovedAttribute key={a.Id}>
                                        <span>{a.Label}</span>
                                        <RemovedAttributeValue>{a.Value.Label}</RemovedAttributeValue>
                                        <RemovedAttributeTag>{T._('Removed', 'Admin')}</RemovedAttributeTag>
                                    </RemovedAttribute>
                                ))}
                            </>
                        )}
                        {notImportedModelOptions.length > 0 && (
                            <>
                                <NotImportedSeparator />
                                <NotImportedHeader>
                                    <strong>{T._('Variants not imported yet', 'Admin')}</strong>
                                    <span>
                                        {T._(
                                            'You can still map these attributes: they will be imported only for the current variant. The variant is not deleted or duplicated, only remapped.',
                                            'Admin'
                                        )}
                                    </span>
                                </NotImportedHeader>
                                {notImportedModelOptions.map(renderAttributeSelect)}
                            </>
                        )}
                        {isMissingAttributes && !isDeleteSuggested && (
                            <ValidationMessageStyled>{T._('At least one field is required', 'Admin')}</ValidationMessageStyled>
                        )}
                        {duplicateVariant && !isDeleteSuggested && (
                            <ValidationMessageStyled>
                                {T._('Another variant is already using the selected attributes. Please choose a different combination.', 'Admin')}
                            </ValidationMessageStyled>
                        )}
                    </>
                ) : dialogMode == 'EditMapping' && merchantProductOptions.length > 0 && merchantAttributeMapping.length > 0 ? (
                    <>
                        <HeaderColumns $xs={2}>
                            <strong>{T._('Main product', 'Admin')}</strong>
                            <strong>{T._('Sales channel product', 'Admin')}</strong>
                        </HeaderColumns>
                        <div>
                            {modelColorCode.map((ma, i) => {
                                let merchantProductOptionsForMapping = [{ label: 'No map', value: '' }].concat(
                                    merchantProductOptions
                                        .find((mpo) => mpo.optionID == merchantAttributeMapping[i].Id)
                                        ?.values.map((o) => {
                                            return { value: o.valueID, label: o.label };
                                        })!
                                );

                                return (
                                    <React.Fragment key={ma.Id}>
                                        <Columns $xs={2}>
                                            <FormControl>
                                                <Input readOnly={true} value={ma.Label}></Input>
                                            </FormControl>
                                            <FormControl>
                                                <AdvancedSelect
                                                    isSearchable={false}
                                                    options={merchantProductAttributes}
                                                    className='comboControl'
                                                    menuPosition='fixed'
                                                    value={merchantProductAttributes.find((mpa) => mpa.value == merchantAttributeMapping[i].Id)}
                                                    onChange={(item) => onChangeAttributeMapping(item, i, merchantProductOptions, modelColorMappingsForRender)}
                                                />
                                            </FormControl>
                                        </Columns>

                                        <Columns $xs={2}>
                                            <FormControl>
                                                <Input readOnly={true} value={ma.Value.Label}></Input>
                                            </FormControl>
                                            <FormControl>
                                                <AdvancedSelect
                                                    isSearchable={false}
                                                    options={merchantProductOptionsForMapping}
                                                    className='comboControl'
                                                    menuPosition='fixed'
                                                    value={{ value: merchantAttributeMapping[i].Value.Id, label: merchantAttributeMapping[i].Value.Label }}
                                                    onChange={(item) => onChangeOptionMapping(item, i, merchantProductOptions, modelColorMappingsForRender)}
                                                />
                                            </FormControl>
                                        </Columns>

                                        <AttributeSeparator></AttributeSeparator>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <>
                        <AdvancedSelect
                            //onChange={selectedOption => handleChangeAttribute(opt, selectedOption?.value ?? '')}
                            menuPosition='fixed'
                            value={{ value: modelColor.guid, label: modelColor.name }}
                            options={[{ value: modelColor.guid, label: modelColor.name }]}
                        />
                    </>
                )}
            </Stack>
        );
    };

    return (
        <Dialog
            windowDecorator={EditVariantDialogWindow}
            title={dialogTitle}
            buttons={[
                isDeleteSuggested
                    ? {
                          label: T._('Delete variant', 'Admin'),
                          destructive: true,
                          onClick: onDelete
                      }
                    : {
                          label: buttonLabel || T._('Confirm ', 'Admin'),
                          onClick: handleConfirm,
                          disabled: isEditingVariantInProgress || isMissingAttributes || !!duplicateVariant
                      }
            ]}
        >
            {dialogMode == 'EditMapping' ? (
                <Suspense
                    fallback={
                        <DialogOverlayEditVariant>
                            <Loader />
                        </DialogOverlayEditVariant>
                    }
                >
                    <EditVariantDialogMerchantOptionsWrapper salesChannelId={salesChannelSelectedID} sku={skuSelected}>
                        {(merchantProductOptions) => renderDialogContent(merchantProductOptions)}
                    </EditVariantDialogMerchantOptionsWrapper>
                </Suspense>
            ) : (
                renderDialogContent([])
            )}
        </Dialog>
    );
};

const EditVariantDialogMerchantOptionsWrapper: FC<EditVariantDialogMerchantOptionsWrapperProps> = ({ sku, salesChannelId, children }) => {
    const { merchantProductOptions } = useLazyLoadQuery<editVariantDialogQuery>(
        graphql`
            query editVariantDialogQuery($sku: String!, $salesChannelID: ID!) {
                merchantProductOptions(sku: $sku, salesChannelID: $salesChannelID) {
                    optionID
                    label
                    values {
                        valueID
                        label
                    }
                }
            }
        `,
        { sku, salesChannelID: salesChannelId }
    );

    return <>{children((merchantProductOptions ?? []) as merchantProductOptionType[])}</>;
};

const EditVariantDialog: FC<EditVariantDialogProps> = (props) => {
    return (
        <ValidationStateProvider>
            <EditVariantDialogContent {...props} />
        </ValidationStateProvider>
    );
};

export default EditVariantDialog;
